import React from "react";
import { ChevronDown, ListFilter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type UsersFiltersState = {
  userQuery: string;
  statuses: string[];
  positions: string[];
  roles: string[];
  groups: string[];
};

export const DEFAULT_USERS_FILTERS: UsersFiltersState = {
  userQuery: "",
  statuses: [],
  positions: [],
  roles: [],
  groups: [],
};

export type UsersFilterOption = {
  value: string;
  label: string;
};

type UsersFiltersPopoverProps = {
  applied: UsersFiltersState;
  positionOptions: UsersFilterOption[];
  roleOptions: UsersFilterOption[];
  groupOptions: UsersFilterOption[];
  onApply: (next: UsersFiltersState) => void;
};

type UsersFilterSelectionKey = "statuses" | "positions" | "roles" | "groups";
type SearchableOptionField = "role" | "group";

const FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const FILTER_COMBOBOX_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

const sectionKeyByField = {
  Status: "status",
  User: "user",
  Position: "position",
  Role: "role",
  Group: "group",
} as const;

export function UsersFiltersPopover({
  applied,
  positionOptions,
  roleOptions,
  groupOptions,
  onApply,
}: UsersFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<UsersFiltersState>(applied);
  const [sectionOpen, setSectionOpen] = React.useState({
    status: false,
    user: true,
    position: false,
    role: false,
    group: false,
  });
  const [optionSearch, setOptionSearch] = React.useState<
    Record<SearchableOptionField, string>
  >({
    role: "",
    group: "",
  });
  const statusOptions = React.useMemo<UsersFilterOption[]>(
    () => [
      {
        value: "active",
        label: t("users.filters.options.status.active"),
      },
      {
        value: "inactive",
        label: t("users.filters.options.status.inactive"),
      },
    ],
    [t],
  );

  const optionLabels = React.useMemo(
    () => ({
      statuses: new Map(
        statusOptions.map((option) => [option.value, option.label]),
      ),
      positions: new Map(
        positionOptions.map((option) => [option.value, option.label]),
      ),
      roles: new Map(roleOptions.map((option) => [option.value, option.label])),
      groups: new Map(
        groupOptions.map((option) => [option.value, option.label]),
      ),
    }),
    [groupOptions, positionOptions, roleOptions, statusOptions],
  );

  const activeCount =
    (draft.userQuery.trim() ? 1 : 0) +
    draft.statuses.length +
    draft.positions.length +
    draft.roles.length +
    draft.groups.length;

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
    setOptionSearch({ role: "", group: "" });
    setSectionOpen((prev) => ({
      ...prev,
      status: applied.statuses.length > 0,
      position: applied.positions.length > 0,
      role: applied.roles.length > 0,
      group: applied.groups.length > 0,
      user: true,
    }));
  }, [applied]);

  const toggleOption = (key: UsersFilterSelectionKey, value: string) => {
    setDraft((prev) => {
      const has = prev[key].includes(value);
      return {
        ...prev,
        [key]: has
          ? prev[key].filter((v) => v !== value)
          : [...prev[key], value],
      };
    });
  };

  const removeChip = (key: UsersFilterSelectionKey, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [key]: prev[key].filter((v) => v !== value),
    }));
  };

  const clearUserQuery = () => {
    setDraft((prev) => ({ ...prev, userQuery: "" }));
  };

  const renderSelectableSection = ({
    field,
    openKey,
    values,
    selected,
    selectedKey,
    searchValue,
    onSearchChange,
  }: {
    field: "Status" | "Position" | "Role" | "Group";
    openKey: "status" | "position" | "role" | "group";
    values: readonly UsersFilterOption[];
    selected: string[];
    selectedKey: UsersFilterSelectionKey;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
  }) => {
    const normalizedSearch = searchValue?.trim().toLocaleLowerCase() ?? "";
    const filteredValues = normalizedSearch
      ? values.filter(
          (option) =>
            option.label.toLocaleLowerCase().includes(normalizedSearch) ||
            option.value.toLocaleLowerCase().includes(normalizedSearch),
        )
      : values;

    return (
      <div className="space-y-2">
        <Button
          type="button"
          variant="span"
          className={FILTER_COMBOBOX_TRIGGER_CLASS}
          onClick={() =>
            setSectionOpen((prev) => ({ ...prev, [openKey]: !prev[openKey] }))
          }
        >
          {t(`users.filters.fields.${field.toLowerCase()}`)}
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-[#737373] transition-transform",
              sectionOpen[openKey] && "rotate-180",
            )}
          />
        </Button>

        {sectionOpen[openKey] ? (
          <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
            {onSearchChange ? (
              <div className="border-b border-[#E5E5E5] p-2">
                <Input
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={t("users.filters.searchPlaceholder")}
                  aria-label={t(`users.filters.searchAria.${openKey}`)}
                  className="h-8"
                />
              </div>
            ) : null}
            <div className="max-h-[176px] overflow-y-auto p-1">
              <div className="space-y-1">
                {filteredValues.map((option) => {
                  const checked = selected.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={cn(
                        "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                        checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() =>
                          toggleOption(selectedKey, option.value)
                        }
                        className={FILTER_CHECKBOX_CLASS}
                      />
                      <span className="text-sm leading-normal font-normal text-[#171717]">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
                {filteredValues.length === 0 ? (
                  <p className="px-2 py-1.5 text-sm text-[#737373]">
                    {t("users.filters.noOptions")}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderUserSection = () => (
    <div className="space-y-2">
      <Button
        type="button"
        variant="span"
        className={FILTER_COMBOBOX_TRIGGER_CLASS}
        onClick={() =>
          setSectionOpen((prev) => ({ ...prev, user: !prev.user }))
        }
      >
        {t("users.filters.fields.user")}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-[#737373] transition-transform",
            sectionOpen.user && "rotate-180",
          )}
        />
      </Button>

      {sectionOpen.user ? (
        <div className="rounded-md border border-[#E5E5E5] bg-white p-2 shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
          <Input
            value={draft.userQuery}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                userQuery: event.target.value,
              }))
            }
            placeholder={t("users.filters.searchPlaceholder")}
            aria-label={t("users.filters.searchAria.user")}
            className="h-8"
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) syncDraftFromApplied();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground h-8 min-h-8 w-fit gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {t("users.filters.button")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base leading-normal font-semibold text-[#171717]">
              {activeCount > 0
                ? t("users.filters.titleWithCount", { count: activeCount })
                : t("users.filters.title")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("users.filters.aria.close")}
            >
              <X className="size-5" />
            </Button>
          </div>

          {activeCount > 0 ? (
            <div className="flex flex-wrap gap-2">
              {draft.userQuery.trim() ? (
                <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]">
                  <span className="truncate">{draft.userQuery.trim()}</span>
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={clearUserQuery}
                    aria-label={t("users.filters.aria.clearUser")}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ) : null}

              {(
                [
                  ["statuses", draft.statuses],
                  ["positions", draft.positions],
                  ["roles", draft.roles],
                  ["groups", draft.groups],
                ] as const
              ).flatMap(([key, values]) =>
                values.map((value) => (
                  <span
                    key={`${key}:${value}`}
                    className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                  >
                    {optionLabels[key].get(value) ?? value}
                    <Button
                      type="button"
                      variant="span"
                      className="rounded p-0.5 hover:bg-[#DDD6FE]"
                      onClick={() => removeChip(key, value)}
                      aria-label={t("users.filters.aria.removeFilter")}
                    >
                      <X className="size-3.5 shrink-0" />
                    </Button>
                  </span>
                )),
              )}
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-0.5">
          <div className="space-y-2">
            {renderSelectableSection({
              field: "Status",
              openKey: sectionKeyByField.Status,
              values: statusOptions,
              selected: draft.statuses,
              selectedKey: "statuses",
            })}
            {renderUserSection()}
            {renderSelectableSection({
              field: "Position",
              openKey: sectionKeyByField.Position,
              values: positionOptions,
              selected: draft.positions,
              selectedKey: "positions",
            })}
            {renderSelectableSection({
              field: "Role",
              openKey: sectionKeyByField.Role,
              values: roleOptions,
              selected: draft.roles,
              selectedKey: "roles",
              searchValue: optionSearch.role,
              onSearchChange: (value) =>
                setOptionSearch((prev) => ({ ...prev, role: value })),
            })}
            {renderSelectableSection({
              field: "Group",
              openKey: sectionKeyByField.Group,
              values: groupOptions,
              selected: draft.groups,
              selectedKey: "groups",
              searchValue: optionSearch.group,
              onSearchChange: (value) =>
                setOptionSearch((prev) => ({ ...prev, group: value })),
            })}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_USERS_FILTERS);
              setOptionSearch({ role: "", group: "" });
              onApply(DEFAULT_USERS_FILTERS);
            }}
          >
            {t("users.filters.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            {t("users.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
